import { formatINR } from '@/utils/format';

interface TrendItem {
  month: string;
  sales?: number;
  revenue?: number;
  purchases?: number;
}

interface RevenueTrendChartProps {
  data?: TrendItem[];
}

const DEFAULT_MONTHS: TrendItem[] = [
  { month: 'Nov', sales: 285000, purchases: 198000 },
  { month: 'Dec', sales: 320000, purchases: 225000 },
  { month: 'Jan', sales: 298000, purchases: 210000 },
  { month: 'Feb', sales: 375000, purchases: 258000 },
  { month: 'Mar', sales: 428500, purchases: 302000 },
  { month: 'Apr', sales: 456800, purchases: 285600 },
];

export default function RevenueTrendChart({ data }: RevenueTrendChartProps) {
  const chartData = (data && data.length > 0)
    ? data.map((d) => ({
        label: d.month,
        sales: Number(d.sales ?? d.revenue ?? 0),
        purchases: Number(d.purchases ?? 0),
      }))
    : DEFAULT_MONTHS.map((d) => ({
        label: d.month,
        sales: Number(d.sales ?? 0),
        purchases: Number(d.purchases ?? 0),
      }));

  const maxVal = Math.max(1, ...chartData.flatMap((m) => [m.sales, m.purchases]));
  const totalSalesInPeriod = chartData.reduce((s, m) => s + m.sales, 0);
  const totalPurchasesInPeriod = chartData.reduce((s, m) => s + m.purchases, 0);
  const netMarginPct = totalSalesInPeriod > 0
    ? (((totalSalesInPeriod - totalPurchasesInPeriod) / totalSalesInPeriod) * 100).toFixed(1)
    : '0';

  return (
    <div className="bg-white border border-[#e2e8f0] rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-[#1e293b]">Revenue Trend</h3>
          <p className="text-xs text-[#64748b] mt-0.5">Last 6 months — Sales vs Purchases</p>
        </div>
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1.5 text-xs text-[#64748b]">
            <span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block" />
            Sales
          </span>
          <span className="flex items-center gap-1.5 text-xs text-[#64748b]">
            <span className="w-3 h-3 rounded-sm bg-amber-400 inline-block" />
            Purchases
          </span>
        </div>
      </div>

      <div className="flex items-end gap-2 h-44">
        {chartData.map((m, idx) => {
          const salesH = Math.round((m.sales / maxVal) * 100);
          const purchH = Math.round((m.purchases / maxVal) * 100);
          return (
            <div key={m.label + idx} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full flex items-end gap-0.5" style={{ height: '160px' }}>
                <div
                  className="flex-1 bg-emerald-500 rounded-t-md transition-all"
                  style={{ height: `${Math.max(salesH, 4)}%` }}
                  title={`Sales: ${formatINR(m.sales)}`}
                />
                <div
                  className="flex-1 bg-amber-400 rounded-t-md transition-all"
                  style={{ height: `${Math.max(purchH, 4)}%` }}
                  title={`Purchases: ${formatINR(m.purchases)}`}
                />
              </div>
              <span className="text-xs text-[#94a3b8]">{m.label}</span>
            </div>
          );
        })}
      </div>

      <div className="flex justify-between text-xs text-[#94a3b8] mt-3 pt-3 border-t border-[#f1f5f9]">
        <span>Sales: <span className="font-medium text-emerald-600">{formatINR(totalSalesInPeriod)}</span></span>
        <span>Purchases: <span className="font-medium text-amber-600">{formatINR(totalPurchasesInPeriod)}</span></span>
        <span>Margin: <span className="font-medium text-[#1e293b]">{netMarginPct}%</span></span>
      </div>
    </div>
  );
}
